import { isConditionSatisfied } from '@playbooks/action-cards-utils'
import { sectionTypes } from '@playbooks/core/src/constants'
import { Section } from '@playbooks/core/src/interfaces/ActionCard'

export type Scope = {
  scopeId: number
  order: number
  sections: Section[]
}

type SeparatorInfo = {
  id: number
  index: number
  order: number
}

type SeparatorMap = Record<number, { data: Section[]; order: number }>

function makeSeparatorScopes({
  isSplitViewEnabled,
  sections,
  displayHeadersCount,
  hideSeparatorsAbility,
  isFunnelView,
  conditionalItems,
}: {
  isSplitViewEnabled?: boolean
  sections: Section[]
  conditionalItems: number[]
  displayHeadersCount?: number | null
  hideSeparatorsAbility?: boolean
  isFunnelView?: boolean
}) {
  const firstSection = sections[0]
  const additionalScope = firstSection?.type !== sectionTypes.SEPARATOR ? 1 : 0

  // Extract separator information
  const separators = extractSeparators(sections, firstSection)
  const numberOfActualSeparators = separators.length
  const headersNumber = typeof displayHeadersCount === 'number' ? displayHeadersCount + additionalScope : undefined

  // Calculate visible headers count
  const headersCount = calculateHeadersCount(headersNumber, hideSeparatorsAbility ?? false, numberOfActualSeparators)

  const hideHeaderTitle = headersCount ? !!(headersCount + additionalScope <= 1 && !isFunnelView) : false

  // Build separator map
  const sepArraysMap = buildSeparatorMap(separators)
  const visiblePinnedSections: Section[] = []

  // Update sepArraysMap and visiblePinnedSections
  populateSeparatorMap(
    sections,
    sepArraysMap,
    visiblePinnedSections,
    conditionalItems,
    hideHeaderTitle,
    isFunnelView ?? false,
  )

  // Build scopes from separator map
  let scopes = buildScopes(sepArraysMap, headersCount, isSplitViewEnabled)

  if (isFunnelView && typeof headersCount === 'number' && headersCount - additionalScope <= 1) {
    scopes = makeVirtualScopes(scopes)

    scopes = scopes.filter(scope => {
      const section = scope.sections.find(sec => sec.type !== sectionTypes.SEPARATOR)!

      return isConditionSatisfied(section?.tags || [], conditionalItems, section.conditionRule!)
    })
  }

  return { visiblePinnedSections, scopes }
}

/**
 * Extract separator information from sections
 */
function extractSeparators(sections: Section[], firstSection?: Section): SeparatorInfo[] {
  let order = 0
  const separators: SeparatorInfo[] = []

  sections.forEach((sec, index) => {
    // Include a virtual separator if the first section is not a separator
    if (index === 0 && firstSection?.idOfSep === -1) {
      separators.push({ id: -1, index: 0, order: -1 })
    } else if (sec.type === sectionTypes.SEPARATOR) {
      separators.push({ id: sec.id, index, order: order++ })
    }
  })

  return separators
}

/**
 * Calculate the number of headers to display
 */
function calculateHeadersCount(
  headersNumber: number | undefined,
  hideSeparatorsAbility: boolean,
  numberOfActualSeparators: number,
): number | undefined {
  if (typeof headersNumber === 'number') {
    return headersNumber
  }
  if (hideSeparatorsAbility) {
    return numberOfActualSeparators
  }
  return undefined
}

/**
 * Build initial separator map structure
 */
function buildSeparatorMap(separators: SeparatorInfo[]): SeparatorMap {
  return separators.reduce<SeparatorMap>((acc, current) => {
    if (!acc[current.id]) {
      acc[current.id] = { data: [], order: current.order }
    }
    return acc
  }, {})
}

/**
 * Populate separator map with sections and collect visible pinned sections
 */
function populateSeparatorMap(
  sections: Section[],
  sepArraysMap: SeparatorMap,
  visiblePinnedSections: Section[],
  conditionalItems: number[],
  hideHeaderTitle: boolean,
  isFunnelView: boolean,
) {
  sections.forEach(sec => {
    if (sec.type === sectionTypes.SEPARATOR) {
      sec.hidden = hideHeaderTitle && !isFunnelView
      sepArraysMap[sec.id]?.data.push(sec)
    } else {
      if (sec.isPinned) {
        visiblePinnedSections.push(sec)
      }
      if (!sec.idOfSep) return
      sepArraysMap[sec.idOfSep]?.data.push(sec)
    }
  })
}

/**
 * Build scopes from separator map
 */
function buildScopes(sepArraysMap: SeparatorMap, headers: number | undefined, isSplitViewEnabled?: boolean): Scope[] {
  const separatorScopes = Object.entries(sepArraysMap)
    .filter(([key, value]) => {
      const hasPlaceholder = !!value.data[0]?.placeholder
      const isZeroSeparator = Number(key) === -1

      if (isSplitViewEnabled) {
        // In split view, only consider sections after the first one (separator) for visibility
        const sections = value.data.slice(1)
        const hasUnpinnedSections = sections.some(sec => !sec.isPinned)

        return hasPlaceholder || hasUnpinnedSections || isZeroSeparator
      }

      const hasSections = value.data.length > 1

      return hasPlaceholder || hasSections || isZeroSeparator
    })
    .map(([key, value]) => ({
      scopeId: Number(key),
      order: value.order,
      sections: value.data,
    }))

  return separatorScopes.sort((a, b) => a.order - b.order).slice(0, headers) // Cut off hidden separators
}

/**
 * Transform scopes into virtual scopes for funnel view
 */
function makeVirtualScopes(scopes: Scope[]): Scope[] {
  let order = 0
  const virtualScopes: Scope[] = []

  for (const scope of scopes) {
    for (const sec of scope.sections) {
      if (sec.type === sectionTypes.SEPARATOR) continue

      virtualScopes.push({
        order: order++,
        scopeId: sec.id,
        sections: [
          {
            id: sec.idOfSep,
            type: sectionTypes.SEPARATOR,
            name: sec.name,
            score: sec.score,
            isOpened: true,
          } as Section,
          sec,
        ],
      })
    }
  }

  return virtualScopes
}

export default makeSeparatorScopes
